import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Trophy, Users, Vote, PenSquare, LayoutGrid, Star, Crown, Medal, User, Mic2, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { API } from '@/App';

function LandingPage() {
  const navigate = useNavigate();
  const [activeCompetition, setActiveCompetition] = useState(null);
  const [topArtists, setTopArtists] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    checkAuth();
    fetchActiveCompetition();
    fetchTopArtists();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
      }
    } catch (error) {
      // Not logged in
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleArtistClick = () => {
    if (currentUser?.is_artist) {
      // Already an artist - go to profile
      navigate(`/profile/${currentUser.user_id}`);
    } else {
      // Not an artist - go to registration
      navigate('/join-talent-show');
    }
  };

  const handleSupporterClick = () => {
    navigate('/voting');
  };

  const fetchActiveCompetition = async () => {
    try {
      const response = await fetch(`${API}/competitions/active`);
      const data = await response.json();
      if (data.event_id) {
        setActiveCompetition(data);
      }
    } catch (error) {
      console.error('Failed to fetch active competition');
    }
  };

  const fetchTopArtists = async () => {
    try {
      const response = await fetch(`${API}/votes/leaderboard`);
      const data = await response.json();
      // Get top 6 artists
      setTopArtists(data.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch top artists');
    }
  };

  const getRankBadge = (rank) => {
    switch (rank) {
      case 0:
        return <Crown className="h-6 w-6 text-yellow-500" />;
      case 1:
        return <Trophy className="h-5 w-5 text-gray-400" />;
      case 2:
        return <Medal className="h-5 w-5 text-amber-700" />;
      default:
        return <span className="text-sm font-bold text-muted-foreground">#{rank + 1}</span>;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Competition Info Banner */}
      {activeCompetition && (
        <div className="relative z-20 py-8 px-4">
          <Card className="max-w-6xl mx-auto glass-panel p-8 border-2 border-secondary/50">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <img 
                  src="https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=400&h=300&fit=crop" 
                  alt="Talent Show Stage"
                  className="w-full md:w-64 h-48 object-cover rounded-xl"
                />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-3xl md:text-4xl font-bold mb-2 text-secondary">{activeCompetition.title}</h2>
                <p className="text-lg text-muted-foreground mb-4">{activeCompetition.description}</p>
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <div className="px-4 py-2 bg-accent/20 rounded-full">
                    <span className="font-bold">Date:</span> {formatDate(activeCompetition.end_date)}
                  </div>
                  <div className="px-4 py-2 bg-secondary/20 rounded-full">
                    <span className="font-bold">Status:</span> {activeCompetition.registration_status === 'open' ? 'Registration Open' : 'Registration Closed'}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center px-4">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-accent/20"></div>
        </div>
        
        <div className="relative z-10 text-center max-w-5xl mx-auto">
          <h1 data-testid="landing-hero-title" className="text-5xl md:text-7xl font-extrabold mb-6 text-glow tracking-tighter">
            Tsarona Talent Show
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Where stars are born. Vote for your favorite artists, watch incredible performances, and be part of the biggest talent competition.
          </p>
          
          {/* Different buttons for logged-in vs logged-out users */}
          {isCheckingAuth ? (
            <div className="h-16" /> // Placeholder while checking auth
          ) : currentUser ? (
            // LOGGED IN USER - Show Artist/Group or Supporter buttons
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground">Welcome back, <span className="text-secondary font-bold">{currentUser.name}</span>!</p>
              <div className="flex gap-6 justify-center flex-wrap">
                <Button
                  data-testid="artist-group-btn"
                  size="lg"
                  className="bg-secondary hover:brightness-110 text-black rounded-full px-10 py-8 font-bold text-lg shadow-[0_0_20px_rgba(34,197,94,0.5)]"
                  onClick={handleArtistClick}
                >
                  <Mic2 className="mr-3 h-6 w-6" />
                  {currentUser.is_artist ? 'My Profile' : 'Artist / Group'}
                </Button>
                <Button
                  data-testid="supporter-btn"
                  size="lg"
                  className="bg-primary hover:scale-105 transition-transform text-white rounded-full px-10 py-8 font-bold text-lg shadow-[0_0_20px_rgba(124,58,237,0.5)]"
                  onClick={handleSupporterClick}
                >
                  <Heart className="mr-3 h-6 w-6" />
                  Supporter
                </Button>
              </div>
              {currentUser.is_artist && (
                <p className="text-sm text-green-400">You are registered as an artist</p>
              )}
            </div>
          ) : (
            // LOGGED OUT USER - Show original buttons
            <div className="flex gap-4 justify-center flex-wrap">
              <Button
                data-testid="join-talent-show-btn"
                size="lg"
                className="bg-secondary hover:brightness-110 text-black rounded-full px-8 py-6 font-bold shadow-[0_0_20px_rgba(34,197,94,0.5)]"
                onClick={() => navigate('/join-talent-show')}
              >
                <Star className="mr-2 h-5 w-5" />
                Join Talent Show
              </Button>
              <Button
                data-testid="get-started-btn"
                size="lg"
                className="bg-primary hover:scale-105 transition-transform text-white rounded-full px-8 py-6 font-bold shadow-[0_0_20px_rgba(124,58,237,0.5)]"
                onClick={() => navigate('/join-talent-show')}
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Get Started
              </Button>
              <Button
                data-testid="login-btn"
                size="lg"
                variant="outline"
                className="border-white/20 hover:bg-white/10 rounded-full px-8 py-6"
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Top 6 Artists Section */}
      {topArtists.length > 0 && (
        <div className="py-16 px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <Card className="glass-panel p-8 border-2 border-primary/30">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold mb-2 tracking-tight">
                  <Trophy className="inline-block mr-3 h-8 w-8 text-yellow-500" />
                  Top 6 Artists
                </h2>
                <p className="text-muted-foreground">Current leaders in the competition</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {topArtists.map((artist, index) => (
                  <div 
                    key={artist.artist_id}
                    className={`relative p-4 rounded-2xl text-center transition-all hover:scale-105 ${
                      index === 0 ? 'bg-yellow-500/20 border-2 border-yellow-500/50' :
                      index === 1 ? 'bg-gray-400/20 border border-gray-400/50' :
                      index === 2 ? 'bg-amber-700/20 border border-amber-700/50' :
                      'bg-white/5 border border-white/10'
                    }`}
                  >
                    <div className="absolute top-2 left-2">
                      {getRankBadge(index)}
                    </div>
                    
                    <div className="w-16 h-16 mx-auto rounded-full overflow-hidden bg-primary/20 mb-3">
                      {artist.artist_picture ? (
                        <img src={artist.artist_picture} alt={artist.artist_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="h-8 w-8 text-primary" />
                        </div>
                      )}
                    </div>
                    
                    <h3 className="font-bold text-sm truncate">{artist.artist_name || `Artist ${artist.artist_id.slice(-6)}`}</h3>
                    <p className="text-xl font-bold text-secondary mt-1">{artist.total_votes}</p>
                    <p className="text-xs text-muted-foreground">votes</p>
                  </div>
                ))}
              </div>

              <div className="text-center mt-8">
                <Button
                  size="lg"
                  className="bg-primary hover:scale-105 transition-transform text-white rounded-full px-8 py-6 font-bold shadow-[0_0_20px_rgba(124,58,237,0.5)]"
                  onClick={() => navigate('/voting')}
                >
                  <Vote className="mr-2 h-5 w-5" />
                  Vote for Your Favorite
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Features Section */}
      <div className="py-24 px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 tracking-tight">Explore Tsarona</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <button
              onClick={() => navigate('/join-talent-show')}
              className="glass-panel rounded-3xl p-8 hover:border-secondary/50 transition-colors group cursor-pointer text-left"
            >
              <div className="bg-secondary/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Star className="h-8 w-8 text-secondary" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Join Talent Show</h3>
              <p className="text-muted-foreground leading-relaxed">
                Register as an artist and showcase your talent to compete for amazing prizes.
              </p>
            </button>

            <button
              onClick={() => navigate('/feed')}
              className="glass-panel rounded-3xl p-8 hover:border-primary/50 transition-colors group cursor-pointer text-left"
            >
              <div className="bg-primary/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <LayoutGrid className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Discover Feeds</h3>
              <p className="text-muted-foreground leading-relaxed">
                Explore posts and performances from talented artists around the world.
              </p>
            </button>

            <button
              onClick={() => navigate('/create-post')}
              className="glass-panel rounded-3xl p-8 hover:border-accent/50 transition-colors group cursor-pointer text-left"
            >
              <div className="bg-accent/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <PenSquare className="h-8 w-8 text-accent" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Create Post</h3>
              <p className="text-muted-foreground leading-relaxed">
                Share your talent with the world. Upload images and showcase your skills.
              </p>
            </button>

            <button
              onClick={() => navigate('/leaderboard')}
              className="glass-panel rounded-3xl p-8 hover:border-yellow-500/50 transition-colors group cursor-pointer text-left"
            >
              <div className="bg-yellow-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Trophy className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Leaderboard</h3>
              <p className="text-muted-foreground leading-relaxed">
                Top artists win prizes, recognition, and opportunities. Follow the live rankings.
              </p>
            </button>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center glass-panel rounded-3xl p-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">Ready to Shine?</h2>
          <p className="text-xl mb-8 text-muted-foreground">
            Whether you're an artist or a fan, there's a place for you at Tsarona.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              size="lg"
              className="bg-secondary hover:brightness-110 text-black rounded-full px-8 py-6 font-bold"
              onClick={() => navigate('/join-talent-show')}
            >
              <Star className="mr-2 h-5 w-5" />
              Join as Artist
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/20 hover:bg-white/10 rounded-full px-8 py-6"
              onClick={() => navigate('/join-talent-show')}
            >
              Register as Fan
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LandingPage;